import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Check, Loader2, Sparkles } from 'lucide-react';
import { useStripe } from '../contexts/StripeContext';
import {
  LIVING_WEBSITE_ENTERPRISE,
  LIVING_WEBSITE_HIGHLIGHTS,
  LIVING_WEBSITE_TIERS,
  type LivingWebsiteTierId,
} from '../lib/livingWebsiteProducts';
import AeoPageHead from '../components/AeoPageHead';
import LivingWebsiteEnterpriseForm, {
  type EnterpriseInquiryIntent,
} from '../components/forms/LivingWebsiteEnterpriseForm';
import { LIVING_WEBSITE_PAGE_FAQS } from '../lib/aeo/faqs';

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.45 },
};

function scrollToEnterpriseInquiry() {
  document.getElementById('enterprise-inquiry')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function LivingWebsitePage() {
  const { openCheckout } = useStripe();
  const [searchParams] = useSearchParams();
  const [loadingTier, setLoadingTier] = useState<LivingWebsiteTierId | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enterpriseIntent, setEnterpriseIntent] = useState<EnterpriseInquiryIntent>('talk');
  const checkoutCancelled = searchParams.get('checkout') === 'cancelled';

  const handleBuy = async (tierId: LivingWebsiteTierId) => {
    setError(null);
    setLoadingTier(tierId);
    try {
      await openCheckout(tierId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Checkout failed. Please try again.');
      setLoadingTier(null);
    }
  };

  const openEnterpriseInquiry = (intent: EnterpriseInquiryIntent) => {
    setEnterpriseIntent(intent);
    scrollToEnterpriseInquiry();
  };

  return (
    <>
      <Helmet>
        <title>Living Website — SELF Labs</title>
        <meta
          name="description"
          content="Get your own living website — an AI avatar that talks visitors through your products and pages. Lite, Pro, and Enterprise plans."
        />
      </Helmet>
      <AeoPageHead path="/living-website" faqs={LIVING_WEBSITE_PAGE_FAQS} />

      <div
        id="living-website"
        data-avatar-target="living-website"
        className="min-h-screen bg-black text-white"
      >
        <div
          className="pointer-events-none fixed inset-0 opacity-40"
          style={{
            background:
              'radial-gradient(ellipse 80% 50% at 50% 0%, var(--glow-purple) 0%, transparent 50%), radial-gradient(ellipse 50% 40% at 15% 75%, var(--glow-orange) 0%, transparent 45%), radial-gradient(ellipse 50% 40% at 85% 65%, var(--glow-blue) 0%, transparent 45%)',
          }}
        />

        <div className="relative px-4 pb-20 pt-28 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl">
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Link
                to="/products"
                className="inline-flex items-center text-sm text-white/50 transition-colors hover:text-white group"
              >
                <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
                Back to Products
              </Link>
            </motion.div>

            {checkoutCancelled && (
              <p className="mt-6 rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-center text-sm text-white/60">
                Checkout was cancelled. Pick a plan below when you&apos;re ready.
              </p>
            )}

            {/* Hero */}
            <section className="mx-auto max-w-3xl pb-16 pt-10 text-center">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-white/80">
                  <Sparkles className="h-3.5 w-3.5" />
                  Living Website
                </span>
                <h1 className="mt-6 text-4xl font-bold tracking-tight sm:text-5xl">
                  Your site, alive
                </h1>
                <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-white/65">
                  A living website lets visitors talk to your AI avatar instead of hunting through
                  menus — exactly like selflabs.xyz. Milan can show your pages, explain your
                  products, and guide people to the right place.
                </p>
                <p className="mx-auto mt-4 max-w-xl text-sm text-white/45">
                  Powered by Role Model AI (Concierge 2.3) + SELF Labs integration. Choose Lite for
                  embed + knowledge, Pro for the full split-panel experience, or Enterprise for
                  multi-brand custom deployments.
                </p>
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="mx-auto mt-10 max-w-3xl"
                >
                  <img
                    src="/image%20(62).png"
                    alt="Living website split panel — AI avatar conversation beside live site pages"
                    className="w-full rounded-2xl border border-white/10 shadow-[0_0_40px_rgba(0,0,0,0.45)]"
                    loading="eager"
                    decoding="async"
                  />
                </motion.div>
              </motion.div>
            </section>

            {/* Highlights */}
            <section className="border-t border-white/10 py-16">
              <div className="mx-auto grid max-w-3xl gap-6 sm:grid-cols-2">
                {LIVING_WEBSITE_HIGHLIGHTS.map((item, i) => (
                  <motion.div
                    key={item.title}
                    {...fadeUp}
                    transition={{ delay: i * 0.06 }}
                    className="rounded-2xl border border-white/10 bg-white/[0.03] p-6"
                  >
                    <item.icon className="mb-4 h-7 w-7 text-white/70" strokeWidth={1.5} />
                    <h2 className="font-semibold">{item.title}</h2>
                    <p className="mt-2 text-sm leading-relaxed text-white/55">{item.text}</p>
                  </motion.div>
                ))}
              </div>
            </section>

            {/* Pricing */}
            <section className="border-t border-white/10 py-16" id="pricing">
              <motion.div {...fadeUp} className="mb-10 text-center">
                <h2 className="text-2xl font-bold">Lite, Pro &amp; Enterprise</h2>
                <p className="mx-auto mt-3 max-w-xl text-white/55">
                  One-time setup + monthly care for Lite and Pro. Enterprise is custom deployment
                  and licensing.
                </p>
              </motion.div>

              {error && (
                <p className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-center text-sm text-red-200">
                  {error}
                </p>
              )}

              <div className="mx-auto grid max-w-4xl gap-6 md:grid-cols-2">
                {LIVING_WEBSITE_TIERS.map((tier, i) => (
                  <motion.div
                    key={tier.id}
                    {...fadeUp}
                    transition={{ delay: i * 0.08 }}
                    className={`relative flex flex-col rounded-2xl border p-8 ${
                      tier.highlighted
                        ? 'border-white/25 bg-white/[0.06] shadow-[0_0_32px_var(--glow-purple),0_0_48px_var(--glow-blue)]'
                        : 'border-white/10 bg-white/[0.03]'
                    }`}
                  >
                    {tier.highlighted && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full border border-white/20 bg-black px-3 py-0.5 text-xs font-medium text-white/80">
                        Most popular
                      </span>
                    )}
                    <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                      {tier.tagline}
                    </p>
                    <h3 className="mt-2 text-2xl font-bold">{tier.name}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-white/55">{tier.description}</p>

                    <div className="mt-6 flex items-baseline gap-2">
                      <span className="text-3xl font-bold">{tier.setupLabel}</span>
                      <span className="text-sm text-white/45">setup</span>
                    </div>
                    <p className="mt-1 text-sm text-white/55">
                      then <span className="font-semibold text-white/80">{tier.monthlyLabel}</span>
                      /month
                    </p>

                    <ul className="mt-8 flex-1 space-y-3 border-t border-white/8 pt-6">
                      {tier.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-2.5 text-sm text-white/55">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-white/40" />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <button
                      type="button"
                      disabled={loadingTier !== null}
                      onClick={() => handleBuy(tier.id)}
                      className={`mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-transform hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100 ${
                        tier.highlighted
                          ? 'bg-white text-black shadow-[0_0_24px_var(--glow-purple)] hover:bg-white/90'
                          : 'border border-white/25 bg-white/5 text-white hover:border-white/35 hover:bg-white/10'
                      }`}
                    >
                      {loadingTier === tier.id ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Opening checkout…
                        </>
                      ) : (
                        `Get ${tier.name}`
                      )}
                    </button>
                  </motion.div>
                ))}
              </div>

              {/* Enterprise card */}
              <motion.div
                {...fadeUp}
                id="enterprise"
                data-avatar-target="living-website-enterprise"
                aria-label="Living Website Enterprise"
                className="mx-auto mt-8 max-w-4xl rounded-2xl border border-white/20 bg-white/[0.04] p-8 sm:p-10"
              >
                <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
                  <div className="max-w-xl">
                    <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                      {LIVING_WEBSITE_ENTERPRISE.tagline}
                    </p>
                    <h3 className="mt-2 text-2xl font-bold">{LIVING_WEBSITE_ENTERPRISE.name}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-white/55">
                      {LIVING_WEBSITE_ENTERPRISE.description}
                    </p>
                    <p className="mt-6 text-2xl font-bold tracking-tight sm:text-3xl">
                      {LIVING_WEBSITE_ENTERPRISE.pricingLabel}
                    </p>
                  </div>
                  <div className="flex w-full flex-col gap-3 sm:max-w-xs lg:shrink-0">
                    <button
                      type="button"
                      onClick={() => openEnterpriseInquiry('talk')}
                      className="inline-flex w-full items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-semibold text-black transition-transform hover:scale-[1.02] hover:bg-white/90"
                    >
                      Talk to Enterprise
                    </button>
                    <button
                      type="button"
                      onClick={() => openEnterpriseInquiry('demo')}
                      className="inline-flex w-full items-center justify-center rounded-full border border-white/25 bg-white/5 px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-white/35 hover:bg-white/10"
                    >
                      Request a Private Demo
                    </button>
                  </div>
                </div>

                <ul className="mt-8 grid gap-3 border-t border-white/10 pt-8 sm:grid-cols-2">
                  {LIVING_WEBSITE_ENTERPRISE.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2.5 text-sm text-white/55">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-white/40" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>

              <p className="mx-auto mt-8 max-w-lg text-center text-xs text-white/40">
                After purchase, our team reaches out within 2 business days to schedule kickoff.
                Need custom scope? Use{' '}
                <button
                  type="button"
                  onClick={() => openEnterpriseInquiry('talk')}
                  className="text-white/60 underline hover:text-white"
                >
                  Talk to Enterprise
                </button>{' '}
                below.
              </p>
            </section>

            {/* Enterprise copy + inquiry */}
            <section
              className="border-t border-white/10 py-16"
              id="enterprise-inquiry"
              data-avatar-target="living-website-enterprise-inquiry"
              aria-label="Enterprise Living Website inquiry"
            >
              <motion.div {...fadeUp} className="mx-auto max-w-3xl">
                <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                  {LIVING_WEBSITE_ENTERPRISE.headline}
                </h2>
                <p className="mt-4 text-xl leading-relaxed text-white/80">
                  {LIVING_WEBSITE_ENTERPRISE.body}
                </p>
                <p className="mt-5 text-sm leading-relaxed text-white/55 sm:text-base">
                  {LIVING_WEBSITE_ENTERPRISE.subcopy}
                </p>

                <div className="mt-12">
                  <h3 className="mb-6 text-lg font-semibold">
                    {enterpriseIntent === 'demo'
                      ? 'Request a Private Demo'
                      : 'Enterprise inquiry'}
                  </h3>
                  <LivingWebsiteEnterpriseForm intent={enterpriseIntent} />
                </div>
              </motion.div>
            </section>

            {/* Demo */}
            <section className="border-t border-white/10 py-16">
              <motion.div {...fadeUp} className="mx-auto max-w-2xl text-center">
                <p className="text-white/55">
                  You&apos;re experiencing a Living Website Pro demo right now — go to{' '}
                  <Link to="/" className="text-white/75 underline underline-offset-2 hover:text-white">
                    the homepage
                  </Link>{' '}
                  and ask Milan to show any product.
                </p>
                <Link
                  to="/role-model-ai"
                  className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10"
                >
                  About Role Model AI
                </Link>
              </motion.div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
